import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Check, Sparkles, Crown } from "lucide-react";
import api from "../lib/api";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../context/AuthContext";

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const planCards = [
  { id: "monthly", name: "Monthly", price: 499, unit: "/month", note: "Use code FIRSTTIME for 10% off", couponable: true },
  { id: "annual", name: "Annual", price: 3600, unit: "/year", note: "= Rs.300/month · save ~Rs.200/mo", couponable: false },
];

const UpgradeModal = ({ open, onClose, onSuccess }) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [plan, setPlan] = useState("monthly");
  const [coupon, setCoupon] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) loadRazorpay(); }, [open]);

  const pay = async () => {
    setBusy(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) { toast({ title: "Failed to load payment gateway", variant: "destructive" }); setBusy(false); return; }
      const { data } = await api.post("/payment/create-order", { plan, coupon });
      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.order_id,
        name: "Files on Radar",
        description: `${plan === "monthly" ? "Monthly" : "Annual"} subscription`,
        prefill: { name: data.name, email: data.email },
        theme: { color: "#2f2b8f" },
        handler: async (resp) => {
          try {
            await api.post("/payment/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
              plan,
            });
            toast({ title: "Subscription active!", description: "You now have unlimited requests." });
            onSuccess && onSuccess();
            onClose();
          } catch (e) {
            toast({ title: "Verification failed", description: "Payment captured but verification failed. Contact support.", variant: "destructive" });
          }
        },
        modal: { ondismiss: () => setBusy(false) },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => { toast({ title: "Payment failed", variant: "destructive" }); setBusy(false); });
      rzp.open();
    } catch (err) {
      toast({ title: "Could not start payment", description: err?.response?.data?.detail || "Try again.", variant: "destructive" });
      setBusy(false);
    }
  };

  const discounted = plan === "monthly" && coupon.trim().toUpperCase() === "FIRSTTIME";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg rounded-2xl">
        <DialogHeader>
          <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50"><Crown className="h-6 w-6 text-indigo-700" /></div>
          <DialogTitle className="text-2xl font-bold text-[#1a1a2e]">Upgrade your plan</DialogTitle>
          <DialogDescription className="text-slate-500">A client has reached the Starter limit of 10 requests. Subscribe for unlimited clients and requests.</DialogDescription>
        </DialogHeader>
        <div className="mt-2 grid grid-cols-2 gap-3">
          {planCards.map((p) => (
            <button key={p.id} onClick={() => setPlan(p.id)} className={`rounded-xl border p-4 text-left transition-all ${plan === p.id ? "border-[#2f2b8f] ring-1 ring-[#2f2b8f] bg-indigo-50/40" : "border-slate-200 hover:border-slate-300"}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-[#1a1a2e]">{p.name}</span>
                {plan === p.id && <Check className="h-4 w-4 text-[#2f2b8f]" />}
              </div>
              <div className="mt-2"><span className="text-2xl font-extrabold text-[#1a1a2e]">Rs.{p.price}</span><span className="text-xs text-slate-400">{p.unit}</span></div>
              <p className="mt-1 text-xs text-slate-500">{p.note}</p>
            </button>
          ))}
        </div>
        {plan === "monthly" && (
          <div className="mt-4 space-y-1.5">
            <Label>Coupon code</Label>
            <Input value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="FIRSTTIME" className="rounded-xl uppercase" />
            {discounted && <p className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600"><Sparkles className="h-3.5 w-3.5" /> 10% off applied — you'll pay Rs.449</p>}
          </div>
        )}
        <Button onClick={pay} disabled={busy} className="mt-6 w-full rounded-xl bg-[#2f2b8f] py-6 text-sm font-semibold text-white hover:bg-[#26236f]">
          {busy ? "Opening checkout..." : `Pay with Razorpay`}
        </Button>
        <p className="mt-2 text-center text-xs text-slate-400">Secured by Razorpay · Live mode</p>
      </DialogContent>
    </Dialog>
  );
};

export default UpgradeModal;
